import { FC } from "react";
import { useTranslation } from "react-i18next";

import { ProductCardChip } from "@/components/Layout/BaseLayouts/ProductCard/ProductCardChip";
import { ProductCardFooterItem } from "@/components/Layout/BaseLayouts/ProductCard/ProductCardFooterItem";
import { DeviceProductCard } from "@/components/Portal/components/PortalDeviceTiles/TilesGrid/Tiles/DeviceProductCard/DeviceProductCard.tsx";
import { getIconForProductId } from "@/components/Portal/components/PortalDeviceTiles/TilesGrid/Tiles/DeviceProductCard/ProductCategoryIcon.tsx";
import { useActivatePurchaseIntent } from "@/hooks/useActivatePurchaseIntent.ts";
import { useAlert } from "@/hooks/useAlert.ts";
import { BasketSummaryResponse } from "@/services/model";
import { AlertTypes } from "@/utils/atoms.ts";
import {
  getFlatHomeBaseDisplayValue,
  TransformedProducts,
} from "@/utils/tariffUtils.ts";

interface PhysicalSimIntentTileProps {
  intent: BasketSummaryResponse;
  processedOptions?: TransformedProducts;
}

/**
 * Tile for a physical SIM purchase intent.
 */
export const PhysicalSimPurchaseIntentTile: FC<PhysicalSimIntentTileProps> = ({
  intent,
  processedOptions,
}) => {
  const { t } = useTranslation(["portal", "translation"]);
  const showAlert = useAlert();
  const { activateIntent, isActivating } = useActivatePurchaseIntent();

  const product =
    intent.productId && processedOptions
      ? processedOptions.processedProductsById[intent.productId]
      : null;

  const icon = getIconForProductId(intent.productId);
  const subtitle =
    product?.baseDisplayValue ??
    getFlatHomeBaseDisplayValue(intent.productId) ??
    "-";

  return (
    <DeviceProductCard
      dataTestid="physical-sim-intent"
      icon={icon}
      title={
        intent.simAlias || t("cockpit.purchaseIntent.physical.defaultAlias")
      }
      subtitle={subtitle}
      chips={
        <ProductCardChip
          tone="info"
          label={t("cockpit.purchaseIntent.status.onTheWay")}
        />
      }
      footer={
        <ProductCardFooterItem
          variant="text-button"
          description={t("cockpit.purchaseIntent.physical.message")}
          label={t("cockpit.purchaseIntent.physical.button")}
          loading={isActivating}
          disabled={isActivating}
          onClick={async () => {
            try {
              await activateIntent(intent);
            } catch (error) {
              showAlert({
                type: AlertTypes.error,
                text: t("Common.generalForm.error.genericError"),
              });
            }
          }}
        />
      }
    />
  );
};
